import React from 'react';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import CustomSelect, { SelectOption } from '../CustomSelect';
import StagedDialog from '../StagedDialog';
import {
  clonePromptSchemaAdditionalProperties,
  PromptSchemaAdditionalProperties,
  PromptSchemaFieldAdvancedOptions,
  PromptSchemaEnumValue,
  PromptSchemaFieldDefinition,
  PromptSchemaFieldType,
  createPromptSchemaField,
  getDuplicatePromptSchemaFieldNames,
  getPromptSchemaFieldValidationError,
  getPromptSchemaFieldStyleCategory,
  isAdditionalPropertiesEnabled,
} from './schemaFields';
import './schemaTreeStyles.css';

interface PromptSchemaTreeBuilderProps {
  fields: PromptSchemaFieldDefinition[];
  onFieldsChange: (fields: PromptSchemaFieldDefinition[]) => void;
  editable?: boolean;
  emptyStateText?: string;
  onFieldNameConflict?: (fieldName: string) => void;
  onFieldNameConflictResolved?: () => void;
  nestingLevel?: number;
  schemaAllowsAdditionalProperties?: PromptSchemaAdditionalProperties;
  onSchemaAllowsAdditionalPropertiesChange?: (value: PromptSchemaAdditionalProperties) => void;
  schemaAdditionalPropertiesLabel?: string;
  showPreview?: boolean;
  showRootAddButton?: boolean;
  mode?: 'simple' | 'advanced';
  newFieldDefaults?: Partial<PromptSchemaFieldDefinition>;
  addFieldPresentation?: 'inline' | 'dialog';
}

const TYPE_OPTIONS: SelectOption[] = [
  { value: 'string', label: 'text' },
  { value: 'integer', label: 'int' },
  { value: 'number', label: 'num' },
  { value: 'boolean', label: 'bool' },
  { value: 'enum', label: 'enum' },
  { value: 'object', label: 'object' },
  { value: 'array_string', label: 'text[]' },
  { value: 'array_integer', label: 'int[]' },
  { value: 'array_number', label: 'num[]' },
  { value: 'array_boolean', label: 'bool[]' },
  { value: 'array_enum', label: 'enum[]' },
  { value: 'array_object', label: 'object[]' },
  { value: 'any', label: 'any' },
];

const NO_DEFAULT = '__no_default__';

// Generate example response JSON from fields
const generateExampleValue = (field: PromptSchemaFieldDefinition): any => {
  const enums = (field.enumValues || []).filter((v): v is string => typeof v === 'string');

  switch (field.type) {
    case 'string':
      return field.defaultValue !== undefined ? field.defaultValue : '...';
    case 'integer':
      return field.defaultValue !== undefined ? Number(field.defaultValue) || 0 : 0;
    case 'number':
      return field.defaultValue !== undefined ? Number(field.defaultValue) || 0.0 : 0.0;
    case 'boolean':
      return field.defaultValue !== undefined ? field.defaultValue : true;
    case 'enum':
      return field.defaultValue !== undefined ? field.defaultValue : (enums[0] || 'option');
    case 'object':
      return generateExampleObject(field.nestedFields || []);
    case 'array_string':
      return ['...'];
    case 'array_integer':
      return [0];
    case 'array_number':
      return [0.0];
    case 'array_boolean':
      return [true];
    case 'array_enum':
      return [enums[0] || 'option'];
    case 'array_object':
      return [generateExampleObject(field.nestedFields || [])];
    case 'any':
      return null;
    default:
      return null;
  }
};

export const generateExampleObject = (fields: PromptSchemaFieldDefinition[]): Record<string, any> => {
  const obj: Record<string, any> = {};
  for (const field of fields) {
    if (field.name) {
      obj[field.name] = generateExampleValue(field);
    }
  }
  return obj;
};

const PromptSchemaTreeBuilder: React.FC<PromptSchemaTreeBuilderProps> = ({
  fields,
  onFieldsChange,
  editable = true,
  emptyStateText = 'No fields',
  onFieldNameConflict,
  onFieldNameConflictResolved,
  nestingLevel = 0,
  schemaAllowsAdditionalProperties,
  onSchemaAllowsAdditionalPropertiesChange,
  schemaAdditionalPropertiesLabel = 'Allow additional properties',
  showPreview = true,
  showRootAddButton = true,
  mode = 'simple',
  newFieldDefaults,
  addFieldPresentation = 'inline',
}) => {
  const [fieldDraft, setFieldDraft] = React.useState<PromptSchemaFieldDefinition[] | null>(null);
  const [fieldStep, setFieldStep] = React.useState(0);
  const [fieldError, setFieldError] = React.useState('');
  const preservedRef = React.useRef<PromptSchemaAdditionalProperties>(undefined);
  const disabledRef = React.useRef<PromptSchemaAdditionalProperties>(
    schemaAllowsAdditionalProperties === undefined ? undefined : false,
  );
  const enabledFromToggleRef = React.useRef(false);
  const preserveNextRef = React.useRef(false);

  React.useEffect(() => {
    if (isAdditionalPropertiesEnabled(schemaAllowsAdditionalProperties)) {
      preservedRef.current = clonePromptSchemaAdditionalProperties(schemaAllowsAdditionalProperties);
      preserveNextRef.current = false;
      return;
    }
    if (preserveNextRef.current) {
      preserveNextRef.current = false;
      return;
    }
    preservedRef.current = undefined;
  }, [schemaAllowsAdditionalProperties]);

  React.useEffect(() => {
    if (schemaAllowsAdditionalProperties === undefined || schemaAllowsAdditionalProperties === false) {
      disabledRef.current = schemaAllowsAdditionalProperties;
      enabledFromToggleRef.current = false;
    }
  }, [schemaAllowsAdditionalProperties]);

  const isObjectType = (t: PromptSchemaFieldType) => t === 'object' || t === 'array_object';
  const isEnumType = (t: PromptSchemaFieldType) => t === 'enum' || t === 'array_enum';
  const isArrayType = (t: PromptSchemaFieldType) => t.startsWith('array_');
  const hasDefault = (t: PromptSchemaFieldType) => ['string', 'integer', 'number', 'boolean', 'enum'].includes(t);
  const isAdvancedMode = mode === 'advanced';

  const getVisibleEnums = (vals?: PromptSchemaEnumValue[]) =>
    (vals || []).filter((v): v is string => typeof v === 'string');

  const updateField = (id: string, updates: Partial<PromptSchemaFieldDefinition>) => {
    if (typeof updates.name === 'string') {
      const next = fields.map(f => f.id === id ? { ...f, name: updates.name! } : f);
      const trimmed = updates.name.trim();
      if (trimmed && getDuplicatePromptSchemaFieldNames(next).includes(trimmed)) {
        onFieldNameConflict?.(updates.name);
      } else {
        onFieldNameConflictResolved?.();
      }
    }

    onFieldsChange(fields.map(f => {
      if (f.id !== id) return f;
      const next = { ...f, ...updates };

      if (isEnumType(next.type)) {
        next.enumValues = [...(updates.enumValues ?? next.enumValues ?? [])];
        next.nestedFields = [];
        next.allowAdditionalProperties = false;
      } else if (isObjectType(next.type)) {
        next.nestedFields = [...(updates.nestedFields ?? next.nestedFields ?? [])];
        next.enumValues = [];
        if (!Object.prototype.hasOwnProperty.call(updates, 'allowAdditionalProperties')) {
          if (updates.type && !isObjectType(f.type)) {
            next.allowAdditionalProperties = false;
          }
        }
      } else {
        next.enumValues = updates.type ? [] : [...(updates.enumValues ?? next.enumValues ?? [])];
        next.nestedFields = [];
        next.allowAdditionalProperties = false;
      }
      return next;
    }));
  };

  const removeField = (id: string) => onFieldsChange(fields.filter(f => f.id !== id));

  const moveField = (id: string, direction: 'up' | 'down') => {
    const index = fields.findIndex(f => f.id === id);
    if (
      index === -1
      || (direction === 'up' && index === 0)
      || (direction === 'down' && index === fields.length - 1)
    ) {
      return;
    }

    const next = [...fields];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    onFieldsChange(next);
  };

  const addField = () => {
    const field = createPromptSchemaField(newFieldDefaults);
    if (addFieldPresentation === 'inline') onFieldsChange([...fields, field]);
    else { setFieldDraft([field]); setFieldStep(0); setFieldError(''); }
  };
  const validateFieldDraft = () => {
    const error = getPromptSchemaFieldValidationError(fieldDraft || [], 'field');
    const duplicates = getDuplicatePromptSchemaFieldNames([...fields, ...(fieldDraft || [])]);
    setFieldError(error || (duplicates.length ? `A field named ${duplicates.join(', ')} already exists.` : ''));
    return !error && duplicates.length === 0;
  };

  const handleAdditionalPropertiesToggle = (checked: boolean) => {
    if (!onSchemaAllowsAdditionalPropertiesChange) {
      return;
    }

    if (!checked) {
      preserveNextRef.current = isAdditionalPropertiesEnabled(schemaAllowsAdditionalProperties);
      const restoreValue = enabledFromToggleRef.current ? disabledRef.current : false;
      enabledFromToggleRef.current = false;
      onSchemaAllowsAdditionalPropertiesChange(restoreValue);
      return;
    }

    const preserved = preservedRef.current;
    disabledRef.current = schemaAllowsAdditionalProperties;
    enabledFromToggleRef.current = true;
    onSchemaAllowsAdditionalPropertiesChange(
      isAdditionalPropertiesEnabled(preserved)
        ? clonePromptSchemaAdditionalProperties(preserved)
        : true,
    );
  };

  const handleTypeChange = (f: PromptSchemaFieldDefinition, type: PromptSchemaFieldType) => {
    const enums = getVisibleEnums(f.enumValues);
    updateField(f.id, {
      type,
      defaultValue: type === 'boolean' ? undefined
        : type === 'enum' && enums.includes(String(f.defaultValue)) ? f.defaultValue
        : undefined,
      enumValues: isEnumType(type) ? enums : [],
      nestedFields: isObjectType(type) ? (f.nestedFields || []) : [],
    });
  };

  const getAdvancedOptionValue = (
    value: PromptSchemaFieldAdvancedOptions[keyof PromptSchemaFieldAdvancedOptions] | undefined,
  ): string => (value === undefined ? '' : String(value));

  const updateAdvancedOption = (
    field: PromptSchemaFieldDefinition,
    key: keyof PromptSchemaFieldAdvancedOptions,
    value: PromptSchemaFieldAdvancedOptions[keyof PromptSchemaFieldAdvancedOptions],
  ) => {
    const nextAdvancedOptions: PromptSchemaFieldAdvancedOptions = {
      ...(field.advancedOptions || {}),
    };

    if (value === '' || value === false || value === undefined) {
      delete nextAdvancedOptions[key];
    } else {
      (
        nextAdvancedOptions as Record<
          keyof PromptSchemaFieldAdvancedOptions,
          PromptSchemaFieldAdvancedOptions[keyof PromptSchemaFieldAdvancedOptions] | undefined
        >
      )[key] = value;
    }

    updateField(field.id, {
      advancedOptions: Object.keys(nextAdvancedOptions).length > 0 ? nextAdvancedOptions : undefined,
    });
  };

  const renderAdvancedOptions = (field: PromptSchemaFieldDefinition): React.ReactNode => {
    if (!editable || !isAdvancedMode) {
      return null;
    }

    const advancedOptions = field.advancedOptions || {};
    const showStringItemOptions = field.type === 'array_string' || field.type === 'array_enum';
    const showNumberItemOptions = field.type === 'array_integer' || field.type === 'array_number';
    const showObjectItemOptions = field.type === 'array_object';
    const showStringOptions = field.type === 'string' || field.type === 'enum' || showStringItemOptions;
    const showNumberOptions = field.type === 'integer' || field.type === 'number' || showNumberItemOptions;
    const showArrayOptions = isArrayType(field.type);
    const showObjectOptions = field.type === 'object' || showObjectItemOptions;
    const stringOptionsLabel = showStringItemOptions ? 'item text' : 'text';
    const numberOptionsLabel = showNumberItemOptions ? 'item range' : 'range';
    const objectOptionsLabel = showObjectItemOptions ? 'item props' : 'props';
    const stringOptionLabelPrefix = `${field.name || 'Field'} ${showStringItemOptions ? 'item ' : ''}`;
    const numberOptionLabelPrefix = `${field.name || 'Field'} ${showNumberItemOptions ? 'item ' : ''}`;
    const objectOptionLabelPrefix = `${field.name || 'Field'} ${showObjectItemOptions ? 'item ' : ''}`;

    if (!showStringOptions && !showNumberOptions && !showArrayOptions && !showObjectOptions) {
      return null;
    }

    return (
      <div className="schema-field-advanced">
        {showStringOptions && (
          <div className="schema-field-advanced-group">
            <span className="schema-field-advanced-label">{stringOptionsLabel}</span>
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.minLength)}
              onChange={(event) => updateAdvancedOption(field, 'minLength', event.target.value)}
              placeholder="min"
              inputMode="numeric"
              aria-label={`${stringOptionLabelPrefix}min length`}
            />
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.maxLength)}
              onChange={(event) => updateAdvancedOption(field, 'maxLength', event.target.value)}
              placeholder="max"
              inputMode="numeric"
              aria-label={`${stringOptionLabelPrefix}max length`}
            />
            <input
              className="schema-field-advanced-input"
              value={advancedOptions.pattern || ''}
              onChange={(event) => updateAdvancedOption(field, 'pattern', event.target.value)}
              placeholder="pattern"
              aria-label={`${stringOptionLabelPrefix}pattern`}
            />
            <input
              className="schema-field-advanced-input"
              value={advancedOptions.format || ''}
              onChange={(event) => updateAdvancedOption(field, 'format', event.target.value)}
              placeholder="format"
              aria-label={`${stringOptionLabelPrefix}format`}
            />
          </div>
        )}

        {showNumberOptions && (
          <div className="schema-field-advanced-group">
            <span className="schema-field-advanced-label">{numberOptionsLabel}</span>
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.minimum)}
              onChange={(event) => updateAdvancedOption(field, 'minimum', event.target.value)}
              placeholder="min"
              inputMode={field.type === 'integer' || field.type === 'array_integer' ? 'numeric' : 'decimal'}
              aria-label={`${numberOptionLabelPrefix}minimum`}
            />
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.maximum)}
              onChange={(event) => updateAdvancedOption(field, 'maximum', event.target.value)}
              placeholder="max"
              inputMode={field.type === 'integer' || field.type === 'array_integer' ? 'numeric' : 'decimal'}
              aria-label={`${numberOptionLabelPrefix}maximum`}
            />
          </div>
        )}

        {showArrayOptions && (
          <div className="schema-field-advanced-group">
            <span className="schema-field-advanced-label">items</span>
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.minItems)}
              onChange={(event) => updateAdvancedOption(field, 'minItems', event.target.value)}
              placeholder="min"
              inputMode="numeric"
              aria-label={`${field.name || 'Field'} min items`}
            />
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.maxItems)}
              onChange={(event) => updateAdvancedOption(field, 'maxItems', event.target.value)}
              placeholder="max"
              inputMode="numeric"
              aria-label={`${field.name || 'Field'} max items`}
            />
            <label className="schema-field-advanced-checkbox">
              <input
                type="checkbox"
                checked={Boolean(advancedOptions.uniqueItems)}
                onChange={(event) => updateAdvancedOption(field, 'uniqueItems', event.target.checked)}
              />
              unique
            </label>
          </div>
        )}

        {showObjectOptions && (
          <div className="schema-field-advanced-group">
            <span className="schema-field-advanced-label">{objectOptionsLabel}</span>
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.minProperties)}
              onChange={(event) => updateAdvancedOption(field, 'minProperties', event.target.value)}
              placeholder="min"
              inputMode="numeric"
              aria-label={`${objectOptionLabelPrefix}min properties`}
            />
            <input
              className="schema-field-advanced-input short"
              value={getAdvancedOptionValue(advancedOptions.maxProperties)}
              onChange={(event) => updateAdvancedOption(field, 'maxProperties', event.target.value)}
              placeholder="max"
              inputMode="numeric"
              aria-label={`${objectOptionLabelPrefix}max properties`}
            />
          </div>
        )}
      </div>
    );
  };

  const renderField = (f: PromptSchemaFieldDefinition, idx: number): React.ReactNode => {
    const showEnum = isEnumType(f.type);
    const showNested = isObjectType(f.type);
    const showDefault = hasDefault(f.type);
    const enums = getVisibleEnums(f.enumValues);

    return (
      <div key={f.id} className="schema-field-group">
        <div className="schema-field">
          {/* Name */}
          <div className="schema-field-name">
            {editable ? (
              <input
                className="schema-field-name-input"
                value={f.name}
                onChange={e => updateField(f.id, { name: e.target.value })}
                placeholder="name"
              />
            ) : (
              <span className="schema-field-name-text">{f.name || 'unnamed'}</span>
            )}
          </div>

          {/* Required */}
          {editable ? (
            <label className={`schema-field-required ${f.required ? 'is-required' : ''}`}>
              <input
                type="checkbox"
                checked={f.required}
                onChange={e => updateField(f.id, { required: e.target.checked })}
              />
              req
            </label>
          ) : f.required ? (
            <span className="schema-field-required-badge" title="Required">*</span>
          ) : null}

          {/* Actions */}
          {editable && (
            <div className="schema-field-actions">
              <button
                type="button"
                className="schema-field-move"
                onClick={() => moveField(f.id, 'up')}
                title="Move up"
                aria-label={`Move ${f.name || 'field'} up`}
                disabled={idx === 0}
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                className="schema-field-move"
                onClick={() => moveField(f.id, 'down')}
                title="Move down"
                aria-label={`Move ${f.name || 'field'} down`}
                disabled={idx === fields.length - 1}
              >
                <ArrowDown size={12} />
              </button>
              <button
                type="button"
                className="schema-field-remove"
                onClick={() => removeField(f.id)}
                title="Remove"
                aria-label={`Remove ${f.name || 'field'}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          )}
          {/* Type */}
          <div className="schema-field-type">
            {editable ? (
              <CustomSelect
                className={`schema-field-type-select field-type ${getPromptSchemaFieldStyleCategory(f.type)}`}
                value={f.type}
                onChange={(value) => handleTypeChange(f, value as PromptSchemaFieldType)}
                options={TYPE_OPTIONS}
                ariaLabel={`Type for ${f.name || 'field'}`}
              />
            ) : (
              <span className={`schema-field-type-badge field-type ${getPromptSchemaFieldStyleCategory(f.type)}`}>
                {TYPE_OPTIONS.find(o => o.value === f.type)?.label || f.type}
              </span>
            )}
          </div>

          {/* Description */}
          <div className="schema-field-desc">
            {editable ? (
              <input
                className="schema-field-desc-input"
                value={f.description}
                onChange={e => updateField(f.id, { description: e.target.value })}
                placeholder="description"
              />
            ) : f.description ? (
              <span className="schema-field-desc-text">{f.description}</span>
            ) : null}
          </div>

        </div>

        {/* Enum options */}
        {showEnum && (
          <div className="schema-field-options">
            <span className="schema-field-options-label">Values</span>
            {editable ? (
              <textarea
                className="schema-field-options-input"
                aria-label={`Values for ${f.name || 'field'}`}
                value={enums.join('\n')}
                onChange={e => updateField(f.id, {
                  enumValues: e.target.value === '' ? [] : e.target.value.split('\n')
                })}
                placeholder="option1&#10;option2&#10;option3"
                rows={3}
              />
            ) : enums.length > 0 ? (
              <div className="schema-field-options-tags">
                {enums.map((v, i) => <span key={i} className="schema-field-options-tag">{v || '""'}</span>)}
              </div>
            ) : null}
          </div>
        )}

        {/* Default value */}
        {editable && showDefault && (
          <div className="schema-field-default">
            <span className="schema-field-default-label">default:</span>
            {f.type === 'boolean' ? (
              <CustomSelect
                className="schema-field-default-select"
                value={f.defaultValue?.toString() || ''}
                onChange={value => updateField(f.id, {
                  defaultValue: value === '' ? undefined : value === 'true'
                })}
                options={[
                  { value: '', label: 'None' },
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
                ariaLabel={`Default value for ${f.name || 'field'}`}
              />
            ) : f.type === 'enum' ? (
              <CustomSelect
                className="schema-field-default-select"
                value={typeof f.defaultValue === 'string' && enums.includes(f.defaultValue)
                  ? `e:${enums.indexOf(f.defaultValue)}` : NO_DEFAULT}
                onChange={value => updateField(f.id, {
                  defaultValue: value === NO_DEFAULT ? undefined
                    : enums[parseInt(value.replace('e:', ''))]
                })}
                options={[
                  { value: NO_DEFAULT, label: 'None' },
                  ...enums.map((option, index) => ({ value: `e:${index}`, label: option || '""' })),
                ]}
                ariaLabel={`Default value for ${f.name || 'field'}`}
              />
            ) : (
              <>
                <input
                  className="schema-field-default-input"
                  value={f.defaultValue === undefined ? '' : String(f.defaultValue)}
                  onChange={e => updateField(f.id, {
                    defaultValue: f.type === 'string' ? e.target.value
                      : e.target.value === '' ? undefined : e.target.value
                  })}
                  placeholder="none"
                  inputMode={f.type === 'integer' ? 'numeric' : f.type === 'number' ? 'decimal' : undefined}
                />
                {f.type === 'string' && f.defaultValue !== undefined && (
                  <button
                    className="schema-field-default-clear"
                    onClick={() => updateField(f.id, { defaultValue: undefined })}
                  >
                    clear
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Structured default (clear only) */}
        {editable && !showDefault && f.defaultValue !== undefined && (
          <div className="schema-field-default">
            <span className="schema-field-default-label">default:</span>
            <input
              className="schema-field-default-input"
              value={JSON.stringify(f.defaultValue)}
              readOnly
              aria-label={`${f.name || 'Field'} default value`}
            />
            <button
              className="schema-field-default-clear"
              onClick={() => updateField(f.id, { defaultValue: undefined })}
            >
              clear
            </button>
          </div>
        )}

        {renderAdvancedOptions(f)}

        {/* Nested fields */}
        {showNested && (
          <div className="schema-field-children">
            <PromptSchemaTreeBuilder
              fields={f.nestedFields || []}
              onFieldsChange={nested => updateField(f.id, { nestedFields: nested })}
              editable={editable}
              emptyStateText="no properties"
              onFieldNameConflict={onFieldNameConflict}
              onFieldNameConflictResolved={onFieldNameConflictResolved}
              nestingLevel={nestingLevel + 1}
              schemaAllowsAdditionalProperties={f.allowAdditionalProperties}
              onSchemaAllowsAdditionalPropertiesChange={v => updateField(f.id, { allowAdditionalProperties: v })}
              showRootAddButton={showRootAddButton}
              mode={mode}
              newFieldDefaults={newFieldDefaults}
              addFieldPresentation={addFieldPresentation}
            />
          </div>
        )}
      </div>
    );
  };

  const exampleJson = fields.length > 0 ? generateExampleObject(fields) : null;

  const showAdditionalPropertiesToggle = (
    schemaAllowsAdditionalProperties !== undefined
    || typeof onSchemaAllowsAdditionalPropertiesChange === 'function'
  );

  return (
    <div className={`schema-editor ${!editable ? 'readonly' : ''}`}>
      {showAdditionalPropertiesToggle && (editable || isAdditionalPropertiesEnabled(schemaAllowsAdditionalProperties)) && (
        <div className={`schema-additional-props ${nestingLevel > 0 ? 'nested' : ''}`}>
          {editable ? (
            <label className="schema-additional-props-toggle">
              <input
                type="checkbox"
                checked={isAdditionalPropertiesEnabled(schemaAllowsAdditionalProperties)}
                onChange={(e) => handleAdditionalPropertiesToggle(e.target.checked)}
              />
              {schemaAdditionalPropertiesLabel}
            </label>
          ) : (
            <span className="schema-additional-props-readonly">
              Additional properties: {isAdditionalPropertiesEnabled(schemaAllowsAdditionalProperties) ? 'allowed' : 'not allowed'}
            </span>
          )}
        </div>
      )}

      {fields.length === 0 ? (
        <div className="schema-empty">{emptyStateText}</div>
      ) : (
        <>
          {fields.map((f, i) => renderField(f, i))}
          {nestingLevel === 0 && showPreview && (
            <div className="schema-preview">
              <span className="schema-preview-label">response preview:</span>
              <pre className="schema-preview-json">{JSON.stringify(exampleJson, null, 2)}</pre>
            </div>
          )}
        </>
      )}

      {editable && (nestingLevel > 0 || showRootAddButton) && (
        <button type="button" className="schema-add-btn" onClick={addField}>
          <span className="schema-add-btn-icon">+</span> add
        </button>
      )}
      {fieldDraft && <StagedDialog title="Add field" description="Define the field, then review its output before adding it."
        steps={['Field definition', 'Review']} step={fieldStep} onClose={() => setFieldDraft(null)} className="schema-field-dialog" layer="editor"
        footer={<>
          <button type="button" className="action-button" onClick={() => fieldStep ? setFieldStep(0) : setFieldDraft(null)}>{fieldStep ? 'Back' : 'Cancel'}</button>
          <button type="button" className="search-button" onClick={() => {
            if (!validateFieldDraft()) return;
            if (fieldStep === 0) setFieldStep(1);
            else { onFieldsChange([...fields, ...fieldDraft]); setFieldDraft(null); }
          }}>{fieldStep === 0 ? 'Review field' : 'Add field'}</button>
        </>}>
        <PromptSchemaTreeBuilder fields={fieldDraft} onFieldsChange={setFieldDraft} editable={fieldStep === 0}
          showRootAddButton={false} showPreview={fieldStep === 1} mode={mode} newFieldDefaults={newFieldDefaults} />
        {fieldError && <p className="error-message" role="alert">{fieldError}</p>}
      </StagedDialog>}
    </div>
  );
};

export default PromptSchemaTreeBuilder;
